import { BaseEditor, Descendant } from 'slate'
import { HistoryEditor } from 'slate-history'
import { ReactEditor } from 'slate-react'

import { IFileSettings } from 'core-components/library/dragNDropFileUploader'

import { TCell } from './TCell'
import { TFontColors } from './TFontColors'
import { TIFrame } from './TIFrame'
import { TIcon, TLinkView } from './Tlink'
import { TSeparator } from './TSeparator'

export type IStyleInfo<T> = {
  label: string
  style: T
  icon: string
}

export enum BLOCK_FORMATS {
  blockQuote = 'blockQuote',
  headingOne = 'headingOne',
  headingTwo = 'headingTwo',
  headingThree = 'headingThree',
  headingFour = 'headingFour',
  headingFive = 'headingFive',
  headingSix = 'headingSix',
  image = 'image',
  listItem = 'listItem',
  numberedList = 'numberedList',
  paragraph = 'paragraph',
  span = 'span',
  code = 'code',
  pre = 'pre',
  bulletedList = 'bulletedList',
  link = 'link',
  figure = 'figure',
  video = 'video',
  separator = 'separator',
  iframe = 'iframe',
  table = 'table',
  tableRow = 'tableRow',
  tableCell = 'tableCell',
  action = 'action',
  expandBlock = 'expandBlock',
  expandBlockTitle = 'expandBlockTitle',
}

export enum INLINE_FORMATS {
  bold = 'bold',
  italic = 'italic',
  underline = 'underline',
  strikethrough = 'strikethrough',
  superscript = 'superscript',
  subscript = 'subscript',
}

export enum MODIFIER_FORMATS {
  left = 'left',
  center = 'center',
  right = 'right',
  justify = 'justify',
}

export enum FONT_COLORS_FORMATS {
  black = '--color-black',
  gray_900 = '--color-gray-900',
  gray_700 = '--color-gray-700',
  gray_500 = '--color-gray-500',
  white = '--color-white',
  danger_main = '--color-danger-main',
  warning_dark = '--color-warning-dark',
  warning_light = '--color-warning-light',
  success_light = '--color-success-light',
  success_main = '--color-success-main',
  success_dark = '--color-success-dark',
  info_main = '--color-info-main',
  link = '--color-link',
  link_hover = '--color-link-hover',
  alternate_light = '--color-alternate-light',
}

export enum ACTION_FORMATS {
  tip = 'tip',
  info = 'info',
  warning = 'warning',
}

export enum COLUMN_FORMATS {
  twoOne = '2-1',
  oneTwo = '1-2',
  threeOfOne = '1-1-1',
  half = 'half',
  single = 'single',
}

export enum VIDEO_TYPES {
  default = 'default',
  youtube = 'youtube',
  sharepoint = 'sharepoint',
  microsoftstream = 'microsoftstream',
  brightcove = 'brightcove',
}

export const ACTION_TYPES: IStyleInfo<ACTION_FORMATS>[] = [
  { label: 'Info', style: ACTION_FORMATS.info, icon: 'kbase kbase-info-alt' },
  { label: 'Tip/HowTo', style: ACTION_FORMATS.tip, icon: 'kbase kbase-tip' },
  { label: 'Warning', style: ACTION_FORMATS.warning, icon: 'kbase kbase-warning-alt' },
]

export const COLUMN_TYPES: IStyleInfo<COLUMN_FORMATS>[] = [
  { label: '2-1', style: COLUMN_FORMATS.twoOne, icon: 'kbase kbase-editor-columns-2-1' },
  { label: '1-2', style: COLUMN_FORMATS.oneTwo, icon: 'kbase kbase-editor-columns-1-2' },
  { label: '1-1-1', style: COLUMN_FORMATS.threeOfOne, icon: 'kbase kbase-editor-columns-1-1-1' },
  { label: 'Half', style: COLUMN_FORMATS.half, icon: 'kbase kbase-editor-columns-half' },
  { label: 'Single', style: COLUMN_FORMATS.single, icon: 'kbase kbase-editor-columns-single' },
]

export const LIST_TYPES = [BLOCK_FORMATS.numberedList, BLOCK_FORMATS.bulletedList]

export const ALIGIN_TYPES: IStyleInfo<MODIFIER_FORMATS>[] = [
  { label: 'start', style: MODIFIER_FORMATS.left, icon: 'kbase kbase-editor-ta-l' },
  { label: 'center', style: MODIFIER_FORMATS.center, icon: 'kbase kbase-editor-ta-c' },
  { label: 'end', style: MODIFIER_FORMATS.right, icon: 'kbase kbase-editor-ta-r' },
  { label: 'justify', style: MODIFIER_FORMATS.justify, icon: 'kbase kbase-editor-ta-j' },
]

export const FONT_TYPES: IStyleInfo<BLOCK_FORMATS>[] = [
  { label: 'Paragraph', style: BLOCK_FORMATS.paragraph, icon: '' },
  { label: 'Heading one', style: BLOCK_FORMATS.headingOne, icon: '' },
  { label: 'Heading two', style: BLOCK_FORMATS.headingTwo, icon: '' },
  { label: 'Heading three', style: BLOCK_FORMATS.headingThree, icon: '' },
  { label: 'Heading four', style: BLOCK_FORMATS.headingFour, icon: '' },
  { label: 'Heading five', style: BLOCK_FORMATS.headingFive, icon: '' },
  { label: 'Heading six', style: BLOCK_FORMATS.headingSix, icon: '' },
]

export const BACKGROUND_COLORS: TFontColors[] = [
  { color: FONT_COLORS_FORMATS.danger_main, classColor: 'content-background-danger-main' },
  { color: FONT_COLORS_FORMATS.warning_dark, classColor: 'content-background-warning-dark' },
  { color: FONT_COLORS_FORMATS.warning_light, classColor: 'content-background-warning-light' },
  { color: FONT_COLORS_FORMATS.success_light, classColor: 'content-background-success-light' },
  { color: FONT_COLORS_FORMATS.success_main, classColor: 'content-background-success-main' },
]

export const FONT_COLORS: TFontColors[] = [
  { color: FONT_COLORS_FORMATS.black, classColor: 'content-background-black' },
  { color: FONT_COLORS_FORMATS.gray_900, classColor: 'content-background-gray-900' },
  { color: FONT_COLORS_FORMATS.gray_700, classColor: 'content-background-gray-700' },
  { color: FONT_COLORS_FORMATS.gray_500, classColor: 'content-background-gray-500' },
  { color: FONT_COLORS_FORMATS.white, classColor: 'content-background-white' },
  ...BACKGROUND_COLORS,
  { color: FONT_COLORS_FORMATS.success_dark, classColor: 'content-background-success-dark' },
  { color: FONT_COLORS_FORMATS.info_main, classColor: 'content-background-info-main' },
  { color: FONT_COLORS_FORMATS.link, classColor: 'content-background-link' },
  { color: FONT_COLORS_FORMATS.link_hover, classColor: 'content-background-link-hover' },
  { color: FONT_COLORS_FORMATS.alternate_light, classColor: 'content-background-alternate-light' },
]

export type TBlockQuoteElement = {
  type: BLOCK_FORMATS.blockQuote
  maxWidth?: string
  width?: string
  margin?: string
  marginLeft?: string
  align?: string
  children: Descendant[]
}

export type TBulletedListElement = {
  type: BLOCK_FORMATS.bulletedList
  maxWidth?: string
  width?: string
  margin?: string
  marginLeft?: string
  align?: string
  children: Descendant[]
}

export type TTableElement = {
  type: BLOCK_FORMATS.table
  marginLeft?: string
  align?: string
  isLayout?: boolean
  children: Descendant[]
}

export type TTableRowElement = {
  type: BLOCK_FORMATS.tableRow
  children: Descendant[]
}

export type TTableCellElement = {
  type: BLOCK_FORMATS.tableCell
  children: Descendant[]
} & TCell

export type TNumberedListElement = {
  type: BLOCK_FORMATS.numberedList
  maxWidth?: string
  width?: string
  margin?: string
  marginLeft?: string
  align?: string
  start?: string
  children: Descendant[]
}

export type THeadingElement = {
  type:
    | BLOCK_FORMATS.headingOne
    | BLOCK_FORMATS.headingTwo
    | BLOCK_FORMATS.headingThree
    | BLOCK_FORMATS.headingFour
    | BLOCK_FORMATS.headingFive
    | BLOCK_FORMATS.headingSix
  align?: string
  marginLeft?: string
  children: Descendant[]
}

export type TListItemElement = { type: BLOCK_FORMATS.listItem; children: Descendant[] }

export type TParagraphElement = {
  type: BLOCK_FORMATS.paragraph
  align?: string
  children: Descendant[]
}

export type TSpanElement = {
  type: BLOCK_FORMATS.span
  color?: string
  marginLeft?: string
  children: Descendant[]
}

export type TImageElement = {
  type: BLOCK_FORMATS.image
  marginLeft?: string
  url?: string
  width?: string
  alt?: string
  caption?: string
  children: TCustomText[]
}

export type TLinkElement = {
  type: BLOCK_FORMATS.link
  marginLeft?: string
  url?: string
  align?: string
  className?: string
  view?: TLinkView
  children: (TCustomText | TImageElement)[]
} & TIcon

export type TCodeElement = {
  type: BLOCK_FORMATS.code
  marginLeft?: string
  children: TCustomText[]
}

export type TPreElement = {
  type: BLOCK_FORMATS.pre
  marginLeft?: string
  children: TCustomText[]
}

export type TIFrameElement = {
  type: BLOCK_FORMATS.iframe
  children: TCustomText[]
} & TIFrame

export type TFigureElement = {
  type: BLOCK_FORMATS.figure
  marginLeft?: string
  height?: string
  align?: string
  children: (TCustomText | TVideoElement | TImageElement | TIFrameElement)[]
}

export type TVideoElement = {
  type: BLOCK_FORMATS.video
  marginLeft?: string
  url?: string
  height?: string
  children: TCustomText[]
}

export interface ISeparatorElement extends TSeparator {
  type: BLOCK_FORMATS.separator
  className?: string
  children: TCustomText[]
}

export type TActionElement = {
  type: BLOCK_FORMATS.action
  marginLeft?: string
  format?: ACTION_FORMATS
  children: TCustomElement[]
}

export type TExpandBlockElement = {
  type: BLOCK_FORMATS.expandBlock
  marginLeft?: string
  isOpen?: boolean
  children: (TExpandBlockTitle | Descendant)[]
}

export type TExpandBlockTitle = {
  type: BLOCK_FORMATS.expandBlockTitle
  children: TCustomText[]
}

export type TCustomBlock = {
  type: string
  // @ts-ignore
  children: Descendant[]
}

export type TCustomElement =
  | TBlockQuoteElement
  | TBulletedListElement
  | THeadingElement
  | TListItemElement
  | TParagraphElement
  | TSpanElement
  | TNumberedListElement
  | TImageElement
  | TLinkElement
  | TCodeElement
  | TPreElement
  | TFigureElement
  | TVideoElement
  | ISeparatorElement
  | TTableElement
  | TTableRowElement
  | TTableCellElement
  | TIFrameElement
  | TActionElement
  | TExpandBlockElement
  | TExpandBlockTitle

export type TExtendedCustomElement = TCustomElement | TCustomBlock

export type TCustomText = {
  backgroundColor?: string
  color?: string
  bold?: boolean
  italic?: boolean
  superscript?: boolean
  subscript?: boolean
  code?: boolean
  underline?: boolean
  strikethrough?: boolean
  /* inline font size in px, applied to the selected text (serialized as `font-size:Npx`) */
  fontSize?: number
  text: string
}

interface CustomBaseEditor extends BaseEditor {
  disabled: boolean
  fileUploadAdapter?: (file: IFileSettings) => Promise<string>
  onFileDownload?: (url: string) => void
  onFileRemove?: (url: string) => void
}

export type CustomEditor = CustomBaseEditor & ReactEditor & HistoryEditor

export interface CustomRenderElementProps<T> {
  children: any
  element: T
  attributes: {
    'data-slate-node': 'element'
    'data-slate-inline'?: true
    'data-slate-void'?: true
    dir?: 'rtl'
    ref: any
  }
}

declare module 'slate' {
  interface CustomTypes {
    Editor: CustomEditor
    Element: TExtendedCustomElement
    Text: TCustomText
    RenderElementProps: CustomRenderElementProps<TExtendedCustomElement>
  }
}

export interface IRichTextEditorRef {
  editor: CustomEditor
  editableRef: HTMLDivElement
}
